import React from "react"
import { useSelector } from "react-redux"

import {
    StoreType,
    SilDeviceType,
    SilDeviceNestedType,
} from "client/store"
import {
    Collapse,
    TableWrapper,
    TableScroller,
    TableContentWrapper,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableCellContentWrapper,
} from "client/components"

import Columns from "./columns"

interface CalculatedDataProps {
    existingSubsystem: SilDeviceType
    calculatedSubsystem: SilDeviceType
}

const rowSize = "sm"

const CalculatedData = ({
    existingSubsystem,
    calculatedSubsystem,
}: CalculatedDataProps) => {
    const nestedDevices = useSelector((state: StoreType) => state.silDevice.nested.newDeviceList)
    const lang = useSelector((state: StoreType) => state.system.lang)

    const columns = Columns()
    let counter = 0
    const columnLength = (nestedDevices && nestedDevices.length) + (existingSubsystem.id ? 2 : 1)
    const style: React.CSSProperties = { width: `${100 / columnLength}%` }

    return (
        <Collapse show title={lang.subsystemData}>
            <TableWrapper>
                <TableScroller>
                    <TableContentWrapper inverted>
                        <thead>
                            <TableRow inverted noHover size={rowSize}>
                                <TableHeaderCell cellWidth="200px">{lang.item}</TableHeaderCell>
                                {
                                    columns.map((column) => (
                                        <TableHeaderCell
                                            key={column.field}
                                            cellWidth="200px"
                                        >
                                            <span>{column.header}</span>
                                        </TableHeaderCell>
                                    ))
                                }
                            </TableRow>
                        </thead>

                        <tbody>
                            {
                                existingSubsystem.id && ( // show only for existing subsystem
                                    <TableRow
                                        inverted
                                        noHover
                                        size={rowSize}
                                        style={style}
                                    >
                                        <TableCell>
                                            <TableCellContentWrapper>{lang.existingSubsystem}</TableCellContentWrapper>
                                        </TableCell>
                                        {
                                            columns.map((column) => (
                                                <TableCell key={column.field}>
                                                    <TableCellContentWrapper>
                                                        {
                                                            column.formatter
                                                                ? column.formatter(existingSubsystem)
                                                                : existingSubsystem[column.field as keyof SilDeviceType]
                                                        }
                                                    </TableCellContentWrapper>
                                                </TableCell>
                                            ))
                                        }
                                    </TableRow>
                                )
                            }

                            <TableRow
                                inverted
                                noHover
                                size={rowSize}
                                style={style}
                            >
                                <TableCell>
                                    <TableCellContentWrapper>{lang.subsystem}</TableCellContentWrapper>
                                </TableCell>
                                {
                                    columns.map((column) => {
                                        const existingData = existingSubsystem[column.field as keyof SilDeviceType]
                                        const calculatedData = calculatedSubsystem[column.field as keyof SilDeviceType]
                                        return (
                                            <TableCell
                                                key={column.field}
                                                dangerType={
                                                    existingSubsystem.id // show "danger" only for existing subsystem
                                                    && existingData !== calculatedData
                                                }
                                            >
                                                <TableCellContentWrapper>
                                                    {
                                                        column.formatter
                                                            ? column.formatter(calculatedSubsystem)
                                                            : calculatedSubsystem[column.field as keyof SilDeviceType]
                                                    }
                                                </TableCellContentWrapper>
                                            </TableCell>
                                        )
                                    })
                                }
                            </TableRow>

                            {
                                nestedDevices.map((nested) => {
                                    if (nested) counter += 1
                                    return nested && (
                                        <TableRow
                                            key={counter}
                                            inverted
                                            noHover
                                            size={rowSize}
                                            style={style}
                                        >
                                            <TableCell>
                                                <TableCellContentWrapper>
                                                    {`${lang.device} ${counter}`}
                                                </TableCellContentWrapper>
                                            </TableCell>
                                            {
                                                columns.map((column) => (
                                                    <TableCell key={column.field}>
                                                        <TableCellContentWrapper>
                                                            {
                                                                column.formatter
                                                                    ? column.formatter(nested)
                                                                    : nested[column.field as keyof SilDeviceNestedType]
                                                            }
                                                        </TableCellContentWrapper>
                                                    </TableCell>
                                                ))
                                            }
                                        </TableRow>
                                    )
                                })
                            }
                        </tbody>
                    </TableContentWrapper>
                </TableScroller>
            </TableWrapper>
        </Collapse>
    )
}
export default CalculatedData
